import { Button } from '@casumo/dojo-react';
import { Close } from '@casumo/dojo-react-icons';
import { memo, useCallback } from 'react';
import useFetchErrorContent from '../../api/hooks/useFetchErrorContent';
import useRegister from '../../api/hooks/useRegister.tsx';
import { WarningIcon } from '../../assets/icons/index.ts';
import Drawer from '../../design-system/drawer.tsx';
import Skeleton from '../../design-system/skeleton.tsx';
import { isRegistrationInFlight, useRegistrationStore } from '../../store/store.ts';
import { TEST_ID } from '../../utils/constants';
import { GtmEvents } from '../../utils/gtm/gtm.ts';

type TRegisterPrimaryButton = {
  handleNext: ({
    gtmPayload,
    redirectTo,
  }: {
    gtmPayload?: Record<string, string>;
    redirectTo?: string;
    canProceed?: boolean;
  }) => void;
  label?: string;
  gtmPayload?: Record<string, string>;
};

const IncorrectIdentificationModalSkeleton = () => (
  <div className="flex flex-col w-full h-full items-start justify-start p-sm tablet:px-lg gap-lg">
    <div className="flex w-full gap-3xs items-center justify-between">
      <WarningIcon />
      <Close
        data-testid="close-button"
        className="cursor-pointer text-white"
        svgProps={{
          height: '24',
          width:  '24',
        }}
      />
    </div>
    <Skeleton height="h-[50px] w-full" />
    <Skeleton height="h-[60px] w-full" />
    <div className="flex justify-center items-end w-full grow p-sm tablet:px-lg ">
      <Skeleton height="h-[50px] w-[90%]" />
    </div>
  </div>
);

const RegisterPrimaryButton = ({
  label,
  handleNext,
}: TRegisterPrimaryButton) => {
  const { setFormData } = useRegistrationStore();
  const { formData, setMetadata, registerError, setRegisterError } =
    useRegistrationStore.getState();
  const { registerUser, isRegistering } = useRegister();

  const handleClick = async () => {
    if (!formData || !setMetadata || isRegistrationInFlight()) return;
    GtmEvents.logMarketingConsent(true);
    const marketingDetails = {
      marketingConsent: {
        casino: {
          contactBySms:              true,
          contactByEmail:            true,
          contactByPhone:            true,
          contactByPost:             true,
          contactByPushNotification: true,
        },
        sports: {
          contactBySms:              true,
          contactByEmail:            true,
          contactByPhone:            true,
          contactByPost:             true,
          contactByPushNotification: true,
        },
      },
      subscribeToMarketingMaterial: true,
    };
    
    setFormData(marketingDetails);
    await registerUser({ ...formData, ...marketingDetails });
  };

  const { data: modalData, loading: errorDataLoading } = useFetchErrorContent(registerError, { skip: !registerError });

  const setDrawerIsOpen = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        setRegisterError(null);
      }
    },
    [setRegisterError],
  );

  const onCloseDrawer = useCallback(() => {
    setRegisterError(null);
    handleNext?.({ gtmPayload: {}, redirectTo: '1', canProceed: true });
  }, [handleNext, setRegisterError]);

  return (
    <>
      <Button
        variant="primary"
        onClick={handleClick}
        disabled={isRegistering}
        data-testid={TEST_ID.BUTTON_PRIMARY}
        width="full"
      >
        {label}
      </Button>
      <Drawer isOpen={Boolean(registerError)} setIsOpen={setDrawerIsOpen}>
        <>
          {errorDataLoading ? (
            <IncorrectIdentificationModalSkeleton />
          ) : (
            <div className="flex flex-col relative grow w-full h-full">
              <div className="flex flex-col grow overflow-y-auto">
                <div className="flex gap-3xs items-center justify-between text-white p-md tablet:px-lg">
                  <WarningIcon />
                  <Close
                    data-testid="close-button"
                    className="cursor-pointer"
                    svgProps={{
                      height: '24',
                      width:  '24',
                    }}
                    onClick={onCloseDrawer}
                  />
                </div>
                <p className="text-heading-lg text-white p-sm pt-xs w-full tablet:px-lg">
                  {modalData?.title}
                </p>
                <div className="flex flex-col gap-2xs p-sm w-full tablet:px-lg grow">
                  <p className="text-white">{modalData?.message}</p>
                </div>
              </div>
              <div
                className={`flex flex-col p-md gap-xs
              bg-gradient-to-t from-purple-90 via-70% via-purple-90 to-purple-90/0`}
              >
                <Button
                  width="full"
                  variant="primary-dark"
                  className="bg-yellow-20"
                  data-testid={TEST_ID.BUTTON_PRIMARY}
                  onClick={onCloseDrawer}
                >
                  {modalData?.btnMainText}
                </Button>
              </div>
            </div>
          )}
        </>
      </Drawer>
    </>
  );
};

const Memoized = memo(RegisterPrimaryButton);
Memoized.displayName = 'RegisterPrimaryButton';

export default Memoized;
